import { useState } from "react";
import { Divider, Grid, InputAdornment, Typography } from "@mui/material";
import moment from "moment";
import "moment/locale/de";
import "moment/locale/en-gb";
import "moment/locale/zh-cn";
import "moment/locale/es-mx";
import "moment/locale/es";
import "moment/locale/fr";

import {
  getLocalStorage,
  setLocalStorage
} from "../../../../components/layout/cookies/CookieFunctions";
import ThemeModeSwitch from "./SubComponents/DisplayTab/ThemeModeSwitch";
import GenericPicker from "../../../../components/utilities/GenericPicker";
import { getEnvironmentVariable } from "../../../../global/environmentVariables";
import LanguageSelector from "../../../../components/utilities/LanguageSelector";
import { getLanguageCookie } from "../../../../language/LanguageFunctions";
import { useTranslation } from "react-i18next";
import HelpButton from "../../../../components/utilities/HelpButton";
import { FlagIcon } from "../../../../components/icons/FlagIcon";

const SUPPORTS_DARK_MODE =
  getEnvironmentVariable("SUPPORTS_DARK_MODE") === "true";

export default function DisplayTab() {
  const [locale, setLocale] = useState(getLocalStorage("locale") || "en-us");

  if (moment.locale() !== locale) {
    moment.locale(locale);
  }

  // Language setup
  const language = getLanguageCookie();
  const { t } = useTranslation("translation", {
    lng: language,
    keyPrefix: "pages.account.display"
  });

  const locales = [
    {
      value: "en-us",
      label: (
        <FlagIcon
          countryCode="US"
          alt={t("usa", {
            defaultValue: "United States",
            keyPrefix: "misc.countries"
          })}
          countryName={t("usa", {
            defaultValue: "United States",
            keyPrefix: "misc.countries"
          })}
        />
      )
    },
    {
      value: "en-gb",
      label: (
        <FlagIcon
          countryCode="GB"
          alt={t("uk", {
            defaultValue: "United Kingdom",
            keyPrefix: "misc.countries"
          })}
          countryName={t("uk", {
            defaultValue: "United Kingdom",
            keyPrefix: "misc.countries"
          })}
        />
      )
    },
    {
      value: "de",
      label: (
        <FlagIcon
          countryCode="DE"
          alt={t("germany", {
            defaultValue: "Germany",
            keyPrefix: "misc.countries"
          })}
          countryName={t("germany", {
            defaultValue: "Germany",
            keyPrefix: "misc.countries"
          })}
        />
      )
    },
    {
      value: "es-mx",
      label: (
        <FlagIcon
          countryCode="MX"
          alt={t("mexico", {
            defaultValue: "Mexico",
            keyPrefix: "misc.countries"
          })}
          countryName={t("mexico", {
            defaultValue: "Mexico",
            keyPrefix: "misc.countries"
          })}
        />
      )
    },
    {
      value: "es-es",
      label: (
        <FlagIcon
          countryCode="ES"
          alt={t("spain", {
            defaultValue: "Spain",
            keyPrefix: "misc.countries"
          })}
          countryName={t("spain", {
            defaultValue: "Spain",
            keyPrefix: "misc.countries"
          })}
        />
      )
    },
    {
      value: "fr",
      label: (
        <FlagIcon
          countryCode="FR"
          alt={t("france", {
            defaultValue: "France",
            keyPrefix: "misc.countries"
          })}
          countryName={t("france", {
            defaultValue: "France",
            keyPrefix: "misc.countries"
          })}
        />
      )
    },
    {
      value: "zh-cn",
      label: (
        <FlagIcon
          countryCode="CN"
          alt={t("china", {
            defaultValue: "China",
            keyPrefix: "misc.countries"
          })}
          countryName={t("china", {
            defaultValue: "China",
            keyPrefix: "misc.countries"
          })}
        />
      )
    }
  ];

  return (
    <Grid container gap={2}>
      <Grid item xs={12} sx={{ mt: 1 }}>
        <Typography variant="h4" fontWeight="700">
          {t("languageAndDate", {
            defaultValue: "Language and Date"
          })}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <GenericPicker
          name="dateTimeFormat"
          label={t("dateTimeFormat", {
            defaultValue: "Date / Time Format"
          })}
          value={locale}
          list={locales}
          inclAny={false}
          handleChange={(e) => {
            if (e.target.value !== null) {
              setLocale(e.target.value);
              setLocalStorage("locale", e.target.value);
            }
          }}
          endAdornment={
            <InputAdornment position="end">
              <HelpButton
                title={t("dateTimeFormatDescription", {
                  defaultValue:
                    "The date and time format used throughout the application. This does not affect the language of the UI text, which is controlled by the Language setting above."
                })}
              />
            </InputAdornment>
          }
          disabled={locales.length <= 1}
        />
      </Grid>

      <Grid item xs={12}>
        <LanguageSelector
          type="displayTab"
          endAdornment={
            <HelpButton
              title={t("languageSettingsDescription", {
                defaultValue:
                  "The language used for the user interface text. This does not affect the date and time format, which is controlled by the Date / Time Format setting below. Some items may take a page reload to update."
              })}
            />
          }
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="body1">
          {t("currentDateTimeFormat", {
            defaultValue:
              "The current date and time are displayed below in the selected format:"
          })}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          fontStyle: "italic"
        }}
      >
        {/* TODO: Replace with a nice card/UI demonstrating various different date time formats */}

        <Typography variant="body1">{moment().format("LLLL")}</Typography>
        <Typography variant="body1">{moment().format("L")}</Typography>
        <Typography variant="body1">{moment().format("LT")}</Typography>
      </Grid>

      {SUPPORTS_DARK_MODE && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ mt: 1, mb: 1 }} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h4" fontWeight="700">
              {t("theme", {
                defaultValue: "Theme"
              })}
            </Typography>
            <Grid item xs={12}>
              <ThemeModeSwitch
                label={t("toggleTheme", {
                  defaultValue: "Toggle Theme",
                  keyPrefix: "buttons"
                })}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
}
